import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CheckCircle2, Lightbulb, Download, RefreshCw } from 'lucide-react';
import { getModeDescription } from './modeDescriptions';
import { getModeExample } from './modeExamples';
import { getModeIllustrativeExample } from './modeIllustrativeExamples';

export default function Phase7FinalRecommendation({ wizardData, onDownload, onRestart }) {
  const selectedMode = wizardData.rankedModes.find(mode => mode.id === wizardData.mainMode);
  
  if (!selectedMode) {
    return (
      <div className="p-8 text-center text-slate-500">
        <p>No mode selected. Please go back and select a procurement mode.</p>
      </div>
    );
  }
  
  const { description, basisPoints, legalRef } = getModeDescription(selectedMode);
  const procType = wizardData.typeSelectionMethod === 'ai' 
    ? wizardData.aiRecommendedType 
    : wizardData.procurementType;

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xl font-bold mb-6 block text-emerald-600 flex items-center gap-2">
          <CheckCircle2 className="w-6 h-6" />
          Recommended Procurement Mode
        </Label>
        
        {/* Mode Name Card */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border-2 border-emerald-500 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold text-emerald-900 dark:text-emerald-300">
              {selectedMode.name}
            </h3>
            <div className="text-4xl">🏆</div>
          </div>
          
          {/* Score and Risk */}
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-emerald-600 text-white rounded-full text-sm font-semibold">
              Score: {selectedMode.score}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              selectedMode.riskLevel === 'Low' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
              selectedMode.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
              selectedMode.riskLevel === 'Medium–High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}>
              Risk: {selectedMode.riskLevel}
            </span>
          </div>
          
          {/* Market Condition Note - if available */}
          {selectedMode.marketNote && (
            <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg">
              <p className="text-sm text-blue-900 dark:text-blue-200 flex items-start gap-2">
                <span className="font-semibold">📊 Market Alignment:</span>
                <span>{selectedMode.marketNote}</span>
              </p>
            </div>
          )}
        </div>
        
        {/* Market Condition Summary - if user selected one */}
        {wizardData.marketCondition && (
          <div className={`mt-4 p-4 rounded-lg border-2 ${
            wizardData.marketCondition === 'robust' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700' :
            wizardData.marketCondition === 'limited' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700' :
            'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
          }`}>
            <p className="text-sm font-semibold mb-2 flex items-center gap-2">
              <span>📈</span>
              <span className={
                wizardData.marketCondition === 'robust' ? 'text-emerald-900 dark:text-emerald-300' :
                wizardData.marketCondition === 'limited' ? 'text-amber-900 dark:text-amber-300' :
                'text-red-900 dark:text-red-300'
              }>
                Market Condition: {wizardData.marketCondition.charAt(0).toUpperCase() + wizardData.marketCondition.slice(1)} {wizardData.marketCondition === 'single' ? 'Source' : ''}
              </span>
            </p>
            <p className={`text-xs ${
              wizardData.marketCondition === 'robust' ? 'text-emerald-800 dark:text-emerald-400' :
              wizardData.marketCondition === 'limited' ? 'text-amber-800 dark:text-amber-400' :
              'text-red-800 dark:text-red-400'
            }`}>
              {wizardData.marketCondition === 'robust' && 'Sufficient number of eligible bidders to ensure effective competition.'}
              {wizardData.marketCondition === 'limited' && 'Restricted number of eligible bidders; competition exists but is constrained.'}
              {wizardData.marketCondition === 'single' && 'Only one eligible bidder; no competition.'}
            </p>
          </div>
        )}
        
        {/* Notice about Process Flow */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
          <p className="text-sm text-blue-900 dark:text-blue-300 flex items-start gap-2">
            <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span><span className="font-semibold">Need the process flow?</span> To view the detailed process flow for this specific procurement mode, please refer to the <span className="font-semibold">&ldquo;Process Flow&rdquo;</span> tab under Zap Tools.</span>
          </p>
        </div>
        
        {/* Basis for Recommendation */}
        <div className="bg-white dark:bg-slate-800 border rounded-lg p-4 sm:p-6 mt-6">
          <h4 className="font-bold text-lg mb-3 text-slate-900 dark:text-slate-100">
            Basis for Recommendation:
          </h4>
          <ul className="space-y-2">
            {basisPoints.map((point, index) => (
              <li key={index} className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                <span className="text-emerald-600 mt-1">•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Legal Reference */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 sm:p-6 mt-6">
          <h4 className="font-bold text-lg mb-2 text-blue-900 dark:text-blue-300">
            Legal Reference:
          </h4>
          <p className="text-slate-700 dark:text-slate-300">
            {legalRef}
          </p>
        </div>
        
        {/* Brief Description (Illustrative Example) */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4 sm:p-6 mt-6">
          <h4 className="font-bold text-lg mb-3 text-amber-900 dark:text-amber-300">
            Brief Description:
          </h4>
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            {getModeExample(selectedMode.id)}
          </p>
        </div>
        
        {/* Award Criteria (LCRB and MEARB) */}
        {selectedMode.id === 'competitive-bidding' && (
          <div className="bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-lg p-4 sm:p-6 mt-6">
            <h4 className="font-bold text-lg mb-3 text-purple-900 dark:text-purple-300">
              Award Criteria:
            </h4>
            <div className="space-y-4">
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-purple-200">
                <p className="font-bold text-purple-900 dark:text-purple-300 mb-2">
                  1. LCRB (Lowest Calculated Responsive Bid)
                </p>
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                  Award is based solely on price. The bidder with the lowest calculated and responsive bid wins. This is the most common variant used for straightforward procurements where quality specifications are clearly defined.
                </p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border-2 border-purple-200">
                <p className="font-bold text-purple-900 dark:text-purple-300 mb-2">
                  2. MEARB (Most Economically Advantageous Responsive Bid)
                </p>
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                  Award considers both price and other factors (quality, efficiency, sustainability, etc.) weighted according to pre-disclosed criteria. Used when additional qualitative factors beyond price significantly affect value for money.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Illustrative Example */}
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-lg p-4 sm:p-6 mt-6">
          <h4 className="font-bold text-lg mb-3 text-green-900 dark:text-green-300">
            Illustrative Example:
          </h4>
          <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
            {getModeIllustrativeExample(selectedMode.id)}
          </p>
        </div>
        
        {/* Procurement Details Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-6">
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded border">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Procurement Type</p>
            <p className="text-sm font-medium capitalize">{procType}</p>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded border">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">ABC Value</p>
            <p className="text-sm font-medium">₱{parseFloat(wizardData.contractValue.replace(/,/g, '')).toLocaleString()}</p>
          </div>
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded border">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Entity Type</p>
            <p className="text-sm font-medium capitalize">
              {wizardData.entityType === 'national' ? 'NGA' : 
               wizardData.entityType === 'gocc' ? 'GOCC' : 
               wizardData.entityType === 'suc' ? 'SUC' :
               wizardData.entityType === 'lgu' ? 'LGU' : 
               wizardData.entityType.toUpperCase()}
            </p>
          </div>
        </div>
        
        {/* Important Notice */}
        <div className="mt-6 p-4 bg-slate-100 dark:bg-slate-800 border-l-4 border-blue-600 rounded">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
            IMPORTANT NOTICE:
          </p>
          <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
            This recommendation is based on RA 12009 (New Government Procurement Act) and its Implementing Rules and Regulations (IRR). Always consult the complete law, implementing rules, and seek advice from your BAC and legal counsel for specific procurement situations.
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button 
            onClick={onDownload}
            variant="default"
            size="lg"
            className="w-full sm:flex-1 bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download Summary
          </Button>
          <Button 
            onClick={onRestart}
            variant="outline"
            size="lg"
            className="w-full sm:flex-1 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Start New Assessment
          </Button>
        </div>
      </div>
    </div>
  );
}
